import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React from 'react';
import { useTranslation } from '@ibk/shared-translation';
import { Icon } from '@ibk/shared-ui';
import { ContractStatus } from '../../types';
import { formatContractDateShort } from '../../utils/contractStatusUtils';
import styles from './BenefitsTimeline.module.css';
const updateStep = (steps, index, status, date) => {
    steps[index] = {
        ...steps[index],
        status,
        date: formatContractDateShort(date ?? null),
        icon: status === 'completed' ? 'checkCircle' : 'status',
    };
};
const markStepsCompleted = (steps, stepIndices, createdDate, contractDate) => {
    stepIndices.forEach(index => {
        const dateToUse = index <= 1 ? createdDate : contractDate;
        updateStep(steps, index, 'completed', dateToUse);
    });
};
const buildLegacyTimelineSteps = (contract) => {
    const contractStatus = contract?.status;
    const contractDate = contract?.startDate;
    const createdDate = contract?.createdAt ?? contract?.startDate;
    const steps = [
        { id: 'request', titleKey: 'benefits.timeline.request.title', status: 'pending', date: null, icon: 'circle' },
        { id: 'processing', titleKey: 'benefits.timeline.processing.title', status: 'pending', date: null, icon: 'circle' },
        { id: 'active', titleKey: 'benefits.timeline.active.title', status: 'pending', date: null, icon: 'circle' },
        {
            id: 'sync-employees',
            titleKey: 'benefits.timeline.sync-employees.title',
            status: 'pending',
            date: null,
            icon: 'circle',
        },
        {
            id: 'setup-values',
            titleKey: 'benefits.timeline.setup-values.title',
            status: 'pending',
            date: null,
            icon: 'circle',
        },
    ];
    if (!contract || !contractStatus) {
        return steps;
    }
    switch (contractStatus) {
        case ContractStatus.CREATED:
        case ContractStatus.SYNC_CONTRACT_DATA:
        case ContractStatus.PENDING:
            updateStep(steps, 0, 'completed', createdDate);
            updateStep(steps, 1, 'current', createdDate);
            break;
        case ContractStatus.SYNC_BENEFICIARIES_PARTNER:
            markStepsCompleted(steps, [0, 1, 2], createdDate, contractDate);
            updateStep(steps, 3, 'current', contractDate);
            break;
        case ContractStatus.ACTIVE:
            markStepsCompleted(steps, [0, 1, 2, 3, 4], createdDate, contractDate);
            break;
        default:
            break;
    }
    return steps;
};
export const BenefitsTimeline = ({ contract, steps, className = '' }) => {
    const { t } = useTranslation();
    const timelineSteps = steps ?? buildLegacyTimelineSteps(contract);
    const getStepIcon = (step) => {
        switch (step.status) {
            case 'completed':
                return _jsx(Icon, { name: 'checkMarkCircle', className: styles.iconCompleted });
            case 'current':
                return _jsx(Icon, { name: 'status', className: styles.iconCurrent });
            case 'pending':
                return _jsx(Icon, { name: 'statusOutline', className: styles.iconPending });
            default:
                return _jsx(Icon, { name: 'statusOutline', className: styles.iconPending });
        }
    };
    return (_jsx("div", { className: `${styles.timeline} ${className}`, "data-testid": 'benefits-timeline', children: timelineSteps.map((step, index) => {
            const statusClassName = 'item' + step.status.charAt(0).toUpperCase() + step.status.slice(1);
            const connectorStatus = step.connectorStatus ?? step.status;
            return (_jsx("div", { className: `${styles.timelineItem} ${styles[statusClassName]}`, "data-testid": `timeline-step-${step.id}`, children: _jsxs("div", { className: styles.timelineContent, children: [_jsxs("div", { className: styles.iconContainer, children: [getStepIcon(step), index < timelineSteps.length - 1 && (_jsx("div", { "data-testid": `timeline-connector-${step.id}`, className: `${styles.connector} ${connectorStatus === 'completed' ? styles.connectorCompleted : styles.connectorPending}` }))] }), _jsx("div", { className: styles.contentContainer, children: _jsxs("div", { className: styles.titleContainer, children: [_jsx("h3", { className: styles.stepTitle, children: t(step.titleKey) }), step.status === 'current' ? (_jsx("div", { className: styles.stepLoading })) : (step.date && _jsx("span", { className: styles.stepDate, children: step.date }))] }) })] }) }, step.id));
        }) }));
};
